import { Box } from "@mantine/core"
import { MonthPickerInput } from "@mantine/dates"
import { CalendarIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import "dayjs/locale/mn"
import "dayjs/locale/ko"
import "dayjs/locale/en"
import "dayjs/locale/zh"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import { usePermission } from "../../../components/permission"
import { DeniedCard } from "../../../components/ui"
import classes from "./analytics.module.css"
import { HorizontalBarList } from "./cards/HorizontalBarList"
import { IncompleteOrders } from "./cards/IncompleteOrders"
import { MarginTrendChart } from "./cards/MarginTrendChart"
import { ReturnsTrendChart } from "./cards/ReturnsTrendChart"
import { TrendChart } from "./cards/TrendChart"
import { EmployeeFilter } from "./filters/EmployeeFilter"
import {
  useLowMarginOrders,
  useMarginByBrand,
  useMarginByCustomer,
  useMarginByEmployee,
  useMarginByProduct,
  useMarginSummary,
  useMarginTrend,
  useReturnsByCustomer,
  useReturnsByEmployee,
  useReturnsByProduct,
  useReturnsSummary,
  useReturnsTrend,
  useSalesAnalyticsByCategory,
  useSalesAnalyticsIncompleteOrders,
  useSalesAnalyticsSummary,
  useSalesAnalyticsTopCustomers,
  useSalesAnalyticsTopProducts,
  useSalesAnalyticsTrend
} from "./hooks"
import {
  LowMarginOrdersCard,
  MarginByBrandCard,
  MarginByCustomerCard,
  MarginByEmployeeCard,
  MarginByProductCard
} from "./sections/MarginCards"
import { ProfitabilitySection } from "./sections/ProfitabilitySection"
import { ReturnsByCustomerCard, ReturnsByEmployeeCard, ReturnsByProductCard, ReturnsSummarySection } from "./sections/ReturnsSection"
import { SalesSection } from "./sections/SalesSection"
import { TopCustomersCard } from "./sections/TopCustomersCard"
import { TopProductsCard } from "./sections/TopProductsCard"
import { ReasonBreakdownChart, RefundChannelBreakdownChart, ResolutionBreakdownChart, ResolutionTimeCard, RepeatReturnersCard } from "../../../components/return/dashboard"

const DATE_RE = /^(\d{4})-(\d{1,2})$/

function parseDateParam(d: string | null): { year: number; month: number } {
  const m = d && DATE_RE.exec(d)
  if (m) return { year: parseInt(m[1], 10), month: parseInt(m[2], 10) }
  const now = dayjs()
  return { year: now.year(), month: now.month() + 1 }
}

/** Employee + month pickers. They live in the page header (canvas Ord_Dashboard)
 *  and talk to SalesAnalytics through the URL (?emp=, ?d=). */
export function AnalyticsControls() {
  const { t, i18n } = useTranslation(["dashboard"])
  const dayjsLocale = i18n.language === "zh" ? "zh-cn" : i18n.language
  const [searchParams, setSearchParams] = useSearchParams()
  const { year, month } = parseDateParam(searchParams.get("d"))
  const employeeId = searchParams.get("emp")

  const setEmployee = (next: string | null) => {
    const params = new URLSearchParams(searchParams)
    if (next) params.set("emp", next)
    else params.delete("emp")
    setSearchParams(params, { replace: true })
  }

  const setDate = (next: Date | null) => {
    if (!next) return
    const value = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}`
    const params = new URLSearchParams(searchParams)
    params.set("d", value)
    setSearchParams(params, { replace: true })
  }

  return (
    <>
      <EmployeeFilter value={employeeId} onChange={setEmployee} />
      <MonthPickerInput
        value={new Date(year, month - 1, 1)}
        onChange={(v) => setDate(v ? new Date(v) : null)}
        valueFormat={t("dateFormat")}
        locale={dayjsLocale}
        leftSection={<CalendarIcon size={16} />}
        w={200}
        aria-label={t("selectedMonth")}
      />
    </>
  )
}

export function SalesAnalytics() {
  const { t } = useTranslation(["dashboard"])

  const [searchParams, setSearchParams] = useSearchParams()
  const { year, month } = parseDateParam(searchParams.get("d"))
  const compareYoY = searchParams.get("yoy") === "1"
  const employeeId = searchParams.get("emp")

  const returnsFromIso = dayjs(new Date(year, month - 1, 1)).startOf("month").toISOString()
  const returnsToIso = dayjs(new Date(year, month - 1, 1)).endOf("month").toISOString()

  const toggleYoY = () => {
    const params = new URLSearchParams(searchParams)
    if (compareYoY) params.delete("yoy")
    else params.set("yoy", "1")
    setSearchParams(params, { replace: true })
  }

  const ORD_DASH = usePermission("ORD_DASH")
  // BE margin-* endpoints answer 403 without ERP_PGH_MARGIN_VIEW (cost and margin are restricted).
  const MARGIN = usePermission("ERP_PGH_MARGIN_VIEW")
  const marginAccess = ORD_DASH.hasAccess && MARGIN.hasAccess
  const { data, isLoading } = useSalesAnalyticsSummary({ year, month }, ORD_DASH.hasAccess, employeeId)
  const { data: trendData, isLoading: trendLoading } = useSalesAnalyticsTrend({ year, month }, compareYoY, ORD_DASH.hasAccess, employeeId)
  const { data: topCustomers, isLoading: topCustomersLoading } = useSalesAnalyticsTopCustomers({ year, month }, ORD_DASH.hasAccess, employeeId)
  const { data: topProducts,  isLoading: topProductsLoading  } = useSalesAnalyticsTopProducts({ year, month }, ORD_DASH.hasAccess, employeeId)
  const { data: byCategory,   isLoading: byCategoryLoading   } = useSalesAnalyticsByCategory({ year, month }, ORD_DASH.hasAccess, employeeId)
  const { data: incomplete,   isLoading: incompleteLoading   } = useSalesAnalyticsIncompleteOrders({ year, month }, ORD_DASH.hasAccess, employeeId)

  const { data: marginSummary,   isLoading: marginSummaryLoading   } = useMarginSummary({ year, month }, marginAccess, employeeId)
  const { data: marginTrend,     isLoading: marginTrendLoading     } = useMarginTrend({ year, month }, compareYoY, marginAccess, employeeId)
  const { data: marginByCust,    isLoading: marginByCustLoading    } = useMarginByCustomer({ year, month }, marginAccess, employeeId)
  const { data: marginByProd,    isLoading: marginByProdLoading    } = useMarginByProduct({ year, month }, marginAccess, employeeId)
  const { data: marginByBrand,   isLoading: marginByBrandLoading   } = useMarginByBrand({ year, month }, marginAccess, employeeId)
  const { data: marginByEmp,     isLoading: marginByEmpLoading     } = useMarginByEmployee({ year, month }, marginAccess, employeeId)
  const { data: lowMargin,       isLoading: lowMarginLoading       } = useLowMarginOrders({ year, month }, marginAccess, employeeId)

  const { data: returnsSummary,  isLoading: returnsSummaryLoading  } = useReturnsSummary({ year, month }, ORD_DASH.hasAccess, employeeId)
  const { data: returnsTrend,    isLoading: returnsTrendLoading    } = useReturnsTrend({ year, month }, ORD_DASH.hasAccess, employeeId)
  const { data: returnsProd,     isLoading: returnsProdLoading     } = useReturnsByProduct({ year, month }, ORD_DASH.hasAccess, employeeId)
  const { data: returnsCust,     isLoading: returnsCustLoading     } = useReturnsByCustomer({ year, month }, ORD_DASH.hasAccess, employeeId)
  const { data: returnsEmp,      isLoading: returnsEmpLoading      } = useReturnsByEmployee({ year, month }, ORD_DASH.hasAccess, employeeId)

  const categoryItems = (byCategory?.data.rows ?? []).map(r => ({
    id: r.categoryId,
    label: r.name,
    amount: r.amount,
    percent: r.percent
  }))

  return (
    <Box>
      <SalesSection data={data} isLoading={isLoading} />

      <Box className={classes.stack}>
      <TrendChart
        data={trendData}
        isLoading={trendLoading}
        compareYoY={compareYoY}
        onToggleYoY={toggleYoY}
        currentYear={year}
        title={t("analytics.trend.salesTitle", { defaultValue: "Борлуулалтын трэнд" })}
        subtitle={t("analytics.trend.salesSubtitle", { defaultValue: "Сарын борлуулалтын дүн · Энэ жил vs Өмнөх жил" })}
      />
      </Box>

      <Box className={classes.grid2}>
        <TopCustomersCard data={topCustomers} isLoading={topCustomersLoading} />
        <TopProductsCard  data={topProducts}  isLoading={topProductsLoading} />
      </Box>

      <Box className={classes.grid2}>
        <HorizontalBarList
          title={t("analytics.category.title", { defaultValue: "Барааны бүлгээр" })}
          subtitle={t("analytics.category.subtitle", { defaultValue: "Борлуулалтын задаргаа" })}
          items={categoryItems}
          isLoading={byCategoryLoading}
        />
        <IncompleteOrders data={incomplete} isLoading={incompleteLoading} />
      </Box>

      {/* ===== АШИГТ АЖИЛЛАГАА (Margin section) ===== */}
      {marginAccess ? (
        <>
      <ProfitabilitySection data={marginSummary} isLoading={marginSummaryLoading} />

      <Box className={classes.stack}>
      <MarginTrendChart
        data={marginTrend}
        isLoading={marginTrendLoading}
        compareYoY={compareYoY}
        onToggleYoY={toggleYoY}
        currentYear={year}
      />
      </Box>

      <Box className={classes.grid2}>
        <MarginByCustomerCard data={marginByCust} isLoading={marginByCustLoading} />
        <MarginByProductCard  data={marginByProd} isLoading={marginByProdLoading} />
      </Box>

      <Box className={classes.grid2}>
        <MarginByBrandCard    data={marginByBrand} isLoading={marginByBrandLoading} />
        <MarginByEmployeeCard data={marginByEmp}   isLoading={marginByEmpLoading} />
      </Box>

      <Box className={classes.section}>
        <LowMarginOrdersCard data={lowMargin} isLoading={lowMarginLoading} />
      </Box>
        </>
      ) : (
        <Box className={classes.section}>
          <DeniedCard permission="ERP_PGH_MARGIN_VIEW" title={t("analytics.sections.profit.title")} />
        </Box>
      )}

      {/* ===== БУЦААЛТ (Returns section) ===== */}
      <ReturnsSummarySection data={returnsSummary} isLoading={returnsSummaryLoading} />

      <Box className={classes.stack}>
        <ReturnsTrendChart data={returnsTrend} isLoading={returnsTrendLoading} />
      </Box>

      <Box className={classes.grid3}>
        <ReturnsByProductCard  data={returnsProd} isLoading={returnsProdLoading} />
        <ReturnsByCustomerCard data={returnsCust} isLoading={returnsCustLoading} />
        <ReturnsByEmployeeCard data={returnsEmp}  isLoading={returnsEmpLoading} />
      </Box>

      {ORD_DASH.hasAccess ? (
        <>
          <Box className={classes.grid2}>
            <ReasonBreakdownChart dateFromIso={returnsFromIso} dateToIso={returnsToIso} />
            <RefundChannelBreakdownChart dateFromIso={returnsFromIso} dateToIso={returnsToIso} />
          </Box>
          <Box className={classes.grid2}>
            <ResolutionBreakdownChart dateFromIso={returnsFromIso} dateToIso={returnsToIso} />
            <ResolutionTimeCard dateFromIso={returnsFromIso} dateToIso={returnsToIso} />
          </Box>
          <Box className={classes.stack}>
            <RepeatReturnersCard dateFromIso={returnsFromIso} dateToIso={returnsToIso} />
          </Box>
        </>
      ) : null}
    </Box>
  )
}
